import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CapacityDataService, CrisisDataService, ErrorResponse } from '@enp/platform-api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { mapErrorResponseApiModelToStoreModel } from '@domain/common';
import {
  CrisisStatusStoreModelEnum,
  mapBypassRemitRequestStoreModelToApiModel,
  mapCheckCrisisResponseApiModelToStoreModel,
  mapCrisisFilesApiModelToStoreModel,
  mapCrisisOutageDetailsApiModelToStoreModel,
  mapCurtailmentAffectedTimescaleIntraDayStoreModel,
  mapCurtailmentAffectedTimescaleLongTermOrDayAheadStoreModel,
  mapExecuteCurtailmentRequestStoreModelToApiModel,
  mapGetCurtailmentResponseMtusApiModelToStoreModel,
  mapPostCrisisMessageRequestStoreModelToApiModel,
} from '@domain/crisis-management';
import { mapMessageApiModelToStoreModel } from '@domain/messages';
import { PermissionStoreModelEnum } from '@domain/profile';

import { RouteSegments } from '@shared/enums';
import { getIsPollingContext } from '@shared/interceptors';
import { CustomDateService } from '@shared/pipes/custom-date';

import { BppActions } from '@stores/backup-power-purchase';
import { CapacityActions } from '@stores/capacity';
import { CoreActions, fromCore } from '@stores/core';

import { CrisisActions } from './crisis.actions';
import { fromCrisis } from './crisis.selector';

const MESSAGES_LIMIT = 4;

@Injectable()
export class CrisisEffects {
  readonly #actions$ = inject(Actions);
  readonly #crisisApiService = inject(CrisisDataService);
  readonly #capacityApiService = inject(CapacityDataService);
  readonly #translateService = inject(TranslateService);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #customDateService = inject(CustomDateService);

  public checkCrisis$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.checkCrisis),
      switchMap(() =>
        this.#crisisApiService.checkCrisis('body', false, { context: getIsPollingContext(true) }).pipe(
          map((res) => mapCheckCrisisResponseApiModelToStoreModel(res)),
          map((checkCrisisResponse) =>
            checkCrisisResponse.status === CrisisStatusStoreModelEnum.ACTIVE
              ? CrisisActions.checkCrisisSuccess()
              : CrisisActions.checkCrisisFail()
          ),
          catchError(() => of(CrisisActions.checkCrisisFail()))
        )
      )
    );
  });

  public checkCrisisFail$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(CrisisActions.checkCrisisFail),
        filter(() => this.#route.snapshot.firstChild?.url.at(0)?.path === RouteSegments.CrisisManagement),
        tap(() =>
          this.#router.navigate([
            RouteSegments.Root,
            RouteSegments.Capacity,
            RouteSegments.OutageManagement,
            RouteSegments.UnplannedOutages,
          ])
        )
      );
    },
    { dispatch: false }
  );

  public getOutageDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getOutageDetails),
      switchMap(({ isPolling }) =>
        this.#crisisApiService
          .getCrisisOutageDetails(undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((outageDetails) =>
              CrisisActions.getOutageDetailsSuccess({
                outageDetails: mapCrisisOutageDetailsApiModelToStoreModel(outageDetails),
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                CrisisActions.getOutageDetailsFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getOutageDetailsFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getOutageDetailsFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getCrisisCurtailment$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisCurtailment),
      switchMap(({ deliveryDayDateTime, isPolling }) =>
        this.#crisisApiService
          .getCrisisCurtailment(this.#customDateService.transformToApiModel(deliveryDayDateTime), undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((curtailment) =>
              CrisisActions.getCrisisCurtailmentSuccess({
                mtus: curtailment.mtus.map((mtu) => mapGetCurtailmentResponseMtusApiModelToStoreModel(mtu)),
                affectedTimescales: {
                  longTerm: mapCurtailmentAffectedTimescaleLongTermOrDayAheadStoreModel(curtailment.affectedTimescales.longTerm),
                  dayAhead: mapCurtailmentAffectedTimescaleLongTermOrDayAheadStoreModel(curtailment.affectedTimescales.dayAhead),
                  intraDay: mapCurtailmentAffectedTimescaleIntraDayStoreModel(curtailment.affectedTimescales.intraDay),
                },
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                CrisisActions.getCrisisCurtailmentFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getCrisisCurtailmentFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisCurtailmentFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public executeCrisisCurtailment$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailment),
      switchMap(({ deliveryDayDateTime, request }) =>
        this.#crisisApiService
          .executeCrisisCurtailment(
            this.#customDateService.transformToApiModel(deliveryDayDateTime),
            mapExecuteCurtailmentRequestStoreModelToApiModel(request)
          )
          .pipe(
            map(() =>
              CrisisActions.executeCrisisCurtailmentSuccess({
                deliveryDayDateTime,
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                CrisisActions.executeCrisisCurtailmentFail({
                  errorCode: error.code,
                  errorMsg: error.message ?? '',
                })
              )
            )
          )
      )
    );
  });

  public executeCrisisCurtailmentSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailmentSuccess),
      switchMap(() => this.#translateService.get('crisis-management.curtailment.toasts.execute-curtailment-success')),
      map((message) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public getCrisisCurtailmentOnExecuteCrisisCurtailmentSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailmentSuccess),
      map(({ deliveryDayDateTime }) =>
        CrisisActions.getCrisisCurtailment({
          deliveryDayDateTime,
          isPolling: false,
        })
      )
    );
  });

  public executeCrisisCurtailmentFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailmentFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('crisis-management.curtailment.toasts.execute-curtailment-fail', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public endUnplannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.endUnplannedOutage),
      switchMap(({ id }) =>
        this.#capacityApiService.endUnplannedOutage(id).pipe(
          map(() => CrisisActions.endUnplannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              CrisisActions.endUnplannedOutageFail({
                errorMsg: mapErrorResponseApiModelToStoreModel(error),
              })
            )
          )
        )
      )
    );
  });

  public endUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.endUnplannedOutageSuccess),
      concatLatestFrom(() =>
        this.#store.select(
          fromCore.selectUserHasPermissions$([
            PermissionStoreModelEnum.VIEW_ICO_DASHBOARD,
            PermissionStoreModelEnum.VIEW_PARTICIPANT_DASHBOARD,
          ])
        )
      ),
      tap(([, hasPermissionToViewDashboard]) =>
        this.#router.navigate(hasPermissionToViewDashboard ? [RouteSegments.Root] : [RouteSegments.Public, RouteSegments.PlatformOverview])
      ),
      map(() => CrisisActions.checkCrisis())
    );
  });

  public endUnplannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.endUnplannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public refreshCurtailmentOnUpdateNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageSuccess),
      concatLatestFrom(() => this.#store.select(fromCrisis.selectCurtailment$)),
      switchMap(([{ connectedToCrisis }, curtailment]) =>
        connectedToCrisis
          ? of(
              curtailment?.deliveryDayDateTime
                ? CrisisActions.getCrisisCurtailment({
                    deliveryDayDateTime: curtailment.deliveryDayDateTime,
                    isPolling: false,
                  })
                : CrisisActions.noPreviousRequestDataFound()
            )
          : of(CrisisActions.noCrisisRefreshNecessary())
      )
    );
  });

  public refreshCurtailmentOnExecuteCrisisCurtailmentSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailmentSuccess),
      concatLatestFrom(() => this.#store.select(fromCrisis.selectCurtailment$)),
      switchMap(([, curtailment]) =>
        of(
          curtailment?.deliveryDayDateTime
            ? CrisisActions.getCrisisCurtailment({
                deliveryDayDateTime: curtailment.deliveryDayDateTime,
                isPolling: false,
              })
            : CrisisActions.noPreviousRequestDataFound()
        )
      )
    );
  });

  public refreshBppPurchaseFormOnUpdateNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageSuccess),
      map(({ connectedToCrisis }) => (connectedToCrisis ? BppActions.refreshBppPurchaseForm() : CrisisActions.noCrisisRefreshNecessary()))
    );
  });

  public refreshBppPurchaseFormOnResubmitBppSubmissionBatchResponse$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionBatchResponse),
      map(() => BppActions.refreshBppPurchaseForm())
    );
  });

  public refreshBppPurchaseFormOnExecuteCurtailmentSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.executeCrisisCurtailmentSuccess),
      map(() => BppActions.refreshBppPurchaseForm())
    );
  });

  public resendRemit$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.resendRemit),
      switchMap(({ direction }) =>
        this.#crisisApiService.resendRemit(direction).pipe(
          map(() => CrisisActions.resendRemitSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              CrisisActions.resendRemitFail({
                errorMsg: mapErrorResponseApiModelToStoreModel(error),
              })
            )
          )
        )
      )
    );
  });

  public resendRemitSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.resendRemitSuccess),
      switchMap(() => this.#translateService.get('crisis-management.remit-reporting.resubmit-success')),
      map((message) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public resendRemitFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.resendRemitFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public bypassRemit$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.bypassRemit),
      switchMap(({ direction, request }) =>
        this.#crisisApiService.bypassRemit(direction, mapBypassRemitRequestStoreModelToApiModel(request)).pipe(
          map(() => CrisisActions.bypassRemitSuccess()),
          catchError(() => of(CrisisActions.bypassRemitFail()))
        )
      )
    );
  });

  public bypassRemitSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.bypassRemitSuccess),
      switchMap(() => this.#translateService.get('crisis-management.bypass-remit-dialog.toasts.success')),
      switchMap((message) => [
        CoreActions.setSuccessToastMessage({ toast: { message } }),
        CrisisActions.getOutageDetails({
          isPolling: false,
        }),
      ])
    );
  });

  public bypassRemitFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.bypassRemitFail),
      switchMap(() => this.#translateService.get('crisis-management.bypass-remit-dialog.toasts.error')),
      switchMap((message) => [CoreActions.setErrorToastMessage({ toast: { message } })])
    );
  });

  public getCrisisMessages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisMessages),
      switchMap(({ isPolling }) =>
        this.#crisisApiService
          .getCrisisMessages(MESSAGES_LIMIT, 0, undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((response) =>
              CrisisActions.getCrisisMessagesSuccess({
                messages: response.entries.map((message) => mapMessageApiModelToStoreModel(message)),
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                CrisisActions.getCrisisMessagesFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getCrisisMessagesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisMessagesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public postCrisisMessage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.postCrisisMessage),
      switchMap(({ message }) =>
        this.#crisisApiService.postCrisisMessage(mapPostCrisisMessageRequestStoreModelToApiModel(message)).pipe(
          map(() => CrisisActions.postCrisisMessageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              CrisisActions.postCrisisMessageFail({
                errorCode: error?.code || '',
                errorMsg: error?.message || '',
              })
            )
          )
        )
      )
    );
  });

  public postCrisisMessageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.postCrisisMessageSuccess),
      switchMap(() => this.#translateService.get('crisis-management.participant-communication.toasts.success')),
      map((message) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public postCrisisMessageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.postCrisisMessageFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('crisis-management.participant-communication.toasts.error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public refreshCrisisMessages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        CrisisActions.postCrisisMessageSuccess,
        CrisisActions.executeCrisisCurtailmentSuccess,
        CrisisActions.resendRemitSuccess,
        CrisisActions.bypassRemitSuccess,
        BppActions.resubmitBppSubmissionBatchResponse
      ),
      map(() =>
        CrisisActions.getCrisisMessages({
          isPolling: false,
        })
      )
    );
  });

  public refreshCrisisMessagesOnSubmitBppPurchaseFormSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForGbMarketSuccess, BppActions.submitBppPurchaseFormForFrMarketSuccess),
      map(({ connectedToCrisis }) =>
        connectedToCrisis
          ? CrisisActions.getCrisisMessages({
              isPolling: false,
            })
          : CrisisActions.noCrisisRefreshNecessary()
      )
    );
  });

  public refreshCrisisMessagesOnUpdateNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageSuccess),
      map(({ connectedToCrisis }) =>
        connectedToCrisis
          ? CrisisActions.getCrisisMessages({
              isPolling: false,
            })
          : CrisisActions.noCrisisRefreshNecessary()
      )
    );
  });

  public getCrisisFiles$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisFiles),
      switchMap(({ isPolling }) =>
        this.#crisisApiService
          .getCrisisFiles(undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((crisisFiles) =>
              CrisisActions.getCrisisFilesSuccess({
                crisisFiles: mapCrisisFilesApiModelToStoreModel(crisisFiles),
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                CrisisActions.getCrisisFilesFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getCrisisFilesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CrisisActions.getCrisisFilesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });
}
